import React, { useEffect, useState } from "react";
import { Avatar, Card, Col, Row, Typography, Skeleton } from "antd";
import axios from "axios";
import { Event, EventResponsibility, Location, ActiveMember } from "@bindeleddet/core/types";
import { CalendarOutlined, EnvironmentOutlined, LockOutlined, TeamOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import fallbackAvatar from "assets/images/fallbackAvatar.png";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUtensils } from "@fortawesome/free-solid-svg-icons";
import { useViewport } from "@bindeleddet/core/hooks";
import { formatDateWithTimeNorwegian } from "helpers/dateUtils";

const { Input: SkeletonInput } = Skeleton;

const { Title, Text, Paragraph } = Typography;

interface Props {
  event: Event | undefined;
  location: Location | null | undefined;
}

const NextEvent: React.FC<Props> = ({ event, location }) => {
  const [activeMember, setActiveMember] = useState<ActiveMember>();
  const [responsibilities, setResponsibilities] = useState<EventResponsibility[]>();
  const [loadingResponsible, setLoadingResponsible] = useState(true);

  const { width } = useViewport();

  const breakpoint = 576;

  useEffect(() => {
    if (!event) return;
    axios
      .get<EventResponsibility[]>(`/events/${event.id}/event-responsibility/`)
      .then(response => setResponsibilities(response.data));
  }, [event]);

  useEffect(() => {
    const responsibleMemberId = responsibilities?.filter(r => r.responsibility_type === 1)[0]?.bl_member;

    if (!responsibleMemberId) {
      if (!responsibilities) return;
      return setLoadingResponsible(false);
    }

    axios
      .get<ActiveMember>(`/internal/members/${responsibleMemberId}/`)
      .then(response => {
        setActiveMember(response.data);
      })
      .finally(() => setLoadingResponsible(false));
  }, [responsibilities]);

  return (
    <>
      {event && (
        <>
          <br />
          <Title level={5}>Neste arrangement</Title>
          <Card>
            <Row gutter={[0, 24]}>
              {width < breakpoint ? null : (
                <Col
                  span={4}
                  style={{ alignItems: "center", justifyContent: "left", display: "flex", paddingLeft: "1em" }}
                >
                  <div style={{ textAlign: "center", lineHeight: "100%" }}>
                    <Paragraph style={{ fontSize: "1.3em", marginBottom: "0.6em" }}>
                      {dayjs(event.start).format("MMM").toUpperCase().slice(0, -1)}
                    </Paragraph>
                    <Paragraph style={{ fontSize: "2.3em", margin: "0", paddingTop: "0" }}>
                      {dayjs(event.start).format("DD")}
                    </Paragraph>
                  </div>
                </Col>
              )}
              <Col
                xs={24}
                sm={8}
                md={8}
                lg={8}
                style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
              >
                <Row>
                  <Text ellipsis>
                    <CalendarOutlined style={{ paddingRight: "0.4em" }} />
                    {formatDateWithTimeNorwegian(event.start).replace(" - ", ", kl. ")}
                  </Text>
                </Row>
                <Row>
                  <Text ellipsis>
                    <EnvironmentOutlined style={{ paddingRight: "0.4em" }} />
                    {location?.name ?? "Ikke bestemt"}
                  </Text>
                </Row>
                <Row>
                  <Text ellipsis>
                    <FontAwesomeIcon icon={faUtensils} style={{ paddingRight: "0.5em" }} />
                    {event.food !== null && event.food !== "" ? event.food : "Ikke bestemt"}
                  </Text>
                </Row>
                <Row>
                  {dayjs().isAfter(dayjs(event.open)) ? ( // TODO fix 2 hour delay
                    <>
                      <Text ellipsis>
                        <TeamOutlined style={{ paddingRight: "0.2em" }} /> {event.registrations}/{event.seats} påmeldte
                      </Text>
                    </>
                  ) : (
                    <>
                      <Text ellipsis>
                        <LockOutlined style={{ marginRight: "0.4em" }} />
                        Ikke åpnet
                      </Text>
                    </>
                  )}
                </Row>
              </Col>
              <Col xs={24} sm={12} md={12} lg={12}>
                <Row wrap={false} style={{ height: "100%" }} align="middle">
                  <Col flex="95px">
                    <Avatar
                      src={activeMember?.profile_picture ?? fallbackAvatar}
                      size={85}
                      style={{ cursor: "pointer", marginBottom: "2px" }}
                    />
                  </Col>
                  <Col flex="auto">
                    <Title level={5} ellipsis>
                      Kontaktperson
                    </Title>
                    <Text strong ellipsis>
                      {activeMember ? activeMember.full_name : loadingResponsible ? <SkeletonInput /> : "Ikke bestemt"}
                    </Text>
                    <br />
                    {activeMember ? (
                      <>
                        <Text italic>
                          <a href={`mailto:${activeMember?.bl_email}`}>{activeMember?.bl_email}</a>
                        </Text>
                        <br />
                        <Text italic>
                          <a href={`tel:${activeMember?.mobile}`}>
                            +47 {activeMember?.mobile.replace(/(\d{3})(\d{2})(\d{3})/, "$1 $2 $3")}
                          </a>
                        </Text>
                      </>
                    ) : (
                      !loadingResponsible && (
                        <Text italic>
                          Henvendeler kan rettes til: <br />
                          <a href="mailto:bedrift@bindeleddet.no">bedrift@bindeleddet.no</a>
                        </Text>
                      )
                    )}
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card>
        </>
      )}
    </>
  );
};

export default NextEvent;
